import type { ClubDirectoryItem } from '@/types/api';
import { getStakesLevelKeys, partitionGameTypes, type StakesLevelKey } from '@/services/stakesLevelService';

export type GameTypeFilterKey = 'nlh' | 'plo' | 'plo5' | 'plo6' | 'mtt' | 'ofc' | 'short' | 'squid' | 'bomb';
export type PlatformFilterKey = 'clubgg' | 'pokerbros' | 'pppoker' | 'upoker' | 'other';

export interface ClubFilters {
  gameTypes: GameTypeFilterKey[];
  platforms: PlatformFilterKey[];
  stakes: StakesLevelKey[];
  searchQuery: string;
}

export const EMPTY_CLUB_FILTERS: ClubFilters = {
  gameTypes: [],
  platforms: [],
  stakes: [],
  searchQuery: '',
};

export const GAME_TYPE_FILTER_OPTIONS: { key: GameTypeFilterKey; label: string }[] = [
  { key: 'nlh', label: 'NLH' },
  { key: 'plo', label: 'PLO' },
  { key: 'plo5', label: 'PLO5' },
  { key: 'plo6', label: 'PLO6' },
  { key: 'mtt', label: 'MTT' },
  { key: 'ofc', label: 'OFC' },
  { key: 'short', label: 'Short Deck' },
  { key: 'squid', label: 'Squid' },
  { key: 'bomb', label: 'Bomb Pot' },
];

export const PLATFORM_FILTER_OPTIONS: { key: PlatformFilterKey; label: string }[] = [
  { key: 'clubgg', label: 'ClubGG' },
  { key: 'pokerbros', label: 'PokerBros' },
  { key: 'pppoker', label: 'PPPoker' },
  { key: 'upoker', label: 'UPoker' },
  { key: 'other', label: 'Other' },
];

export const STAKES_FILTER_OPTIONS: { key: StakesLevelKey; label: string }[] = [
  { key: 'low', label: 'Low' },
  { key: 'mid', label: 'Mid' },
  { key: 'high', label: 'High' },
];

function normalizeGameType(gameType: string): string {
  return gameType.toUpperCase().replace(/\s+/g, '');
}

function normalizeApp(app: string | null): string {
  return (app ?? '').toUpperCase().replace(/\s+/g, '');
}

export function classifyGameType(gameType: string): GameTypeFilterKey | null {
  const normalized = normalizeGameType(gameType);

  if (normalized.includes('SQUID')) {
    return 'squid';
  }
  if (normalized.includes('BOMB')) {
    return 'bomb';
  }
  if (normalized.includes('PLO6')) {
    return 'plo6';
  }
  if (normalized.includes('PLO5')) {
    return 'plo5';
  }
  if (normalized.includes('NLH') || normalized === 'HOLDEM') {
    return 'nlh';
  }
  if (normalized.includes('PLO4') || normalized.includes('PLO') || normalized.includes('OMAHA')) {
    return 'plo';
  }
  if (normalized.includes('MTT') || normalized.includes('TOURNAMENT')) {
    return 'mtt';
  }
  if (normalized.includes('OFC') || normalized.includes('OPENFACE')) {
    return 'ofc';
  }
  if (normalized.includes('SHORT')) {
    return 'short';
  }

  return null;
}

export function classifyPlatform(app: string | null): PlatformFilterKey {
  const normalized = normalizeApp(app);

  if (normalized.includes('CLUBGG')) {
    return 'clubgg';
  }
  if (normalized.includes('POKERBROS')) {
    return 'pokerbros';
  }
  if (normalized.includes('PPPOKER') || normalized === 'PPP') {
    return 'pppoker';
  }
  if (normalized.includes('UPOKER')) {
    return 'upoker';
  }

  return 'other';
}

export function getClubGameTypeKeys(club: ClubDirectoryItem): GameTypeFilterKey[] {
  const { gameTypes } = partitionGameTypes(club.gameTypes);
  const keys = new Set<GameTypeFilterKey>();

  for (const gameType of gameTypes) {
    const key = classifyGameType(gameType);
    if (key) {
      keys.add(key);
    }
  }

  return [...keys];
}

export function getClubStakesKeys(club: ClubDirectoryItem): StakesLevelKey[] {
  const { stakesLabels } = partitionGameTypes(club.gameTypes);
  return getStakesLevelKeys(stakesLabels);
}

export function hasActiveFilters(filters: ClubFilters): boolean {
  return (
    filters.gameTypes.length > 0 ||
    filters.platforms.length > 0 ||
    filters.stakes.length > 0 ||
    filters.searchQuery.trim().length > 0
  );
}

function matchesSearchQuery(club: ClubDirectoryItem, query: string): boolean {
  const normalized = query.trim().toLowerCase();
  if (normalized.length === 0) {
    return true;
  }

  const clubName = (club.clubName ?? '').toLowerCase();
  const clubId = (club.clubId ?? '').toLowerCase();

  return clubName.includes(normalized) || clubId.includes(normalized);
}

export function filterClubs(items: ClubDirectoryItem[], filters: ClubFilters): ClubDirectoryItem[] {
  if (!hasActiveFilters(filters)) {
    return items;
  }

  const searchQuery = filters.searchQuery.trim();
  const selectedGameTypes = new Set(filters.gameTypes);
  const selectedPlatforms = new Set(filters.platforms);
  const selectedStakes = new Set(filters.stakes);

  return items.filter((club) => {
    if (!matchesSearchQuery(club, searchQuery)) {
      return false;
    }

    if (selectedPlatforms.size > 0 && !selectedPlatforms.has(classifyPlatform(club.app))) {
      return false;
    }

    if (selectedStakes.size > 0) {
      const clubStakes = getClubStakesKeys(club);
      if (!clubStakes.some((stakesKey) => selectedStakes.has(stakesKey))) {
        return false;
      }
    }

    if (selectedGameTypes.size > 0) {
      const clubGameTypes = getClubGameTypeKeys(club);
      if (!clubGameTypes.some((gameTypeKey) => selectedGameTypes.has(gameTypeKey))) {
        return false;
      }
    }

    return true;
  });
}
